import React from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Transition,
} from '@headlessui/react';
import { Fragment } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { FiCheck } from 'react-icons/fi';
import { FiLoader } from 'react-icons/fi';
import GenericModal from '../../../../../components/ui/GenericModal';
import { markKidReviewed, MarkKidReviewedPayload } from '../../mutations';
import { KIDOverviewData } from '../../../../../types/pages/raidr.types';
import { sortFundNames } from '../../../../../utilities/customSorting';
import TokenService from '../../../../../services/token-service';

interface KIDInfo {
  kiid_id: string;
  last_edit_timestamp: string;
  last_edited_by: string;
  last_edited_by_name: string;
  document_language: string;
  is_primary_language: boolean;
  publication_timestamp: string;
  share_class: string;
  share_class_name: string;
  version: string;
}

interface UniqueFund {
  fund_id: string;
  fund_id_string: string;
  fund_name: string;
  kids: KIDInfo[];
}

interface ReviewConfirmationDialogBoxProps {
  dialogIsShowing: boolean;
  setDialogIsShowing: (value: boolean) => void;
  fundId: string;
  shareClass: string;
  documentLanguage: string;
  overviewData: KIDOverviewData[];
  backToOverviewLink: string;
  hasEditBtn?: boolean;
  isPriips: boolean;
}

// Helper function to check if a share class is reviewed
const isShareClassReviewed = (version: string): boolean => {
  const versionParts = version.split('.');
  return versionParts[2] === '0';
};

const ReviewConfirmationDialogBox: React.FC<
  ReviewConfirmationDialogBoxProps
> = ({
  dialogIsShowing,
  setDialogIsShowing,
  fundId,
  shareClass,
  documentLanguage,
  overviewData,
  backToOverviewLink,
  hasEditBtn,
  isPriips,
}) => {
    const [reviewComplete, setReviewComplete] = React.useState(false);
    const [selectedFund, setSelectedFund] = React.useState<UniqueFund | null>(
      null,
    );
    const [selectedShareClass, setSelectedShareClass] =
      React.useState<KIDInfo | null>(null);
    const [localFunds, setLocalFunds] = React.useState<UniqueFund[] | null>(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { user_client_id: clientId } = TokenService.getUser()!;

    // Get unique funds from the overview data
    const uniqueFunds = React.useMemo(() => {
      const funds = new Map<string, UniqueFund>();
      overviewData.forEach((overviewItem: KIDOverviewData) => {
        // Only process items with matching document language
        if (overviewItem.document_language !== documentLanguage) return;

        if (!funds.has(overviewItem.fund_id_string)) {
          funds.set(overviewItem.fund_id_string, {
            fund_id: overviewItem.fund_id,
            fund_id_string: overviewItem.fund_id_string,
            fund_name: overviewItem.fund_name,
            kids: [],
          });
        }
        const fund = funds.get(overviewItem.fund_id_string);
        if (fund) {
          // Check if this share class already exists in the kids array
          const existingKidIndex = fund.kids.findIndex(
            (kid) =>
              kid.kiid_id === overviewItem.kiid_id &&
              kid.share_class === overviewItem.share_class,
          );

          // Only add if it doesn't exist
          if (existingKidIndex === -1) {
            fund.kids.push({
              kiid_id: overviewItem.kiid_id,
              last_edit_timestamp: overviewItem.last_edit_timestamp,
              last_edited_by: overviewItem.last_edited_by,
              last_edited_by_name: overviewItem.last_edited_by_name,
              document_language: overviewItem.document_language,
              is_primary_language: overviewItem.is_primary_language,
              publication_timestamp: overviewItem.publication_timestamp,
              share_class: overviewItem.share_class,
              share_class_name: overviewItem.share_class_name,
              version: overviewItem.version,
            });
          }
        }
      });
      // Sort kids by ISIN (share_class) for each fund
      funds.forEach((fund) => {
        fund.kids.sort((a, b) => a.share_class.localeCompare(b.share_class));
      });
      return Array.from(funds.values()).sort((a, b) =>
        sortFundNames(a.fund_id_string, b.fund_id_string),
      );
    }, [overviewData, documentLanguage]);

    // Use localFunds if present, otherwise fallback to uniqueFunds
    const fundsForUI = localFunds ?? uniqueFunds;

    // Get share classes for the selected fund
    const shareClassesForFund = React.useMemo(() => {
      if (!selectedFund) return [];
      return selectedFund.kids;
    }, [selectedFund]);

    // Check if all kids in a fund are reviewed
    const isFundFullyReviewed = React.useMemo(() => {
      if (!selectedFund) return false;
      return selectedFund.kids.every((kid) => isShareClassReviewed(kid.version));
    }, [selectedFund]);

    // Check if the selected share class is reviewed
    const isSelectedShareClassReviewed = React.useMemo(() => {
      if (!selectedShareClass) return false;
      return isShareClassReviewed(selectedShareClass.version);
    }, [selectedShareClass]);

    // Set default fund and share class when component mounts or filteredOverviewData changes
    React.useEffect(() => {
      if (overviewData.length > 0 && !selectedFund) {
        const currentFund = (localFunds ?? uniqueFunds).find(
          (fund) => fund.fund_id_string === fundId,
        );
        if (currentFund) {
          setSelectedFund(currentFund);
        }

        const currentShareClass = currentFund?.kids.find(
          (kid) => kid.share_class === shareClass,
        );
        if (currentShareClass) {
          setSelectedShareClass(currentShareClass);
          // If the share class is already reviewed, show the review complete modal
          if (isShareClassReviewed(currentShareClass.version)) {
            setReviewComplete(true);
          }
        }
      }
    }, [overviewData, fundId, shareClass, uniqueFunds, selectedFund, localFunds]);

    // Reset localFunds when overviewData changes (i.e., after refetch)
    React.useEffect(() => {
      setLocalFunds(null);
    }, [overviewData]);

    // Mark document as reviewed mutation
    const markDocumentReviewedMutation = useMutation({
      mutationFn: async () => {
        const payload: MarkKidReviewedPayload = {
          fund_id: fundId,
          share_class: shareClass,
          document_language: documentLanguage,
        };

        return await markKidReviewed(clientId, payload);
      },

      onSuccess: (data) => {
        if (data.Status === 'success') {
          // Update the local version of the selected share class
          if (selectedFund && selectedShareClass) {
            const currentVersion = selectedShareClass.version;
            const [major] = currentVersion.split('.');
            const newVersion = `${parseInt(major) + 1}.0.0`;

            // Update the selected share class version
            setSelectedShareClass({
              ...selectedShareClass,
              version: newVersion,
            });

            // Update the version in the fund's kids array
            const updatedKids = selectedFund.kids.map((kid) =>
              kid.kiid_id === selectedShareClass.kiid_id &&
                kid.share_class === selectedShareClass.share_class
                ? { ...kid, version: newVersion }
                : kid,
            );

            setSelectedFund({
              ...selectedFund,
              kids: updatedKids,
            });

            // --- Optimistically update localFunds for UI ---
            const baseFunds = localFunds ?? uniqueFunds;
            const newFunds = baseFunds.map((fund) => {
              if (fund.fund_id_string !== selectedFund.fund_id_string)
                return fund;
              return {
                ...fund,
                kids: fund.kids.map((kid) =>
                  kid.kiid_id === selectedShareClass.kiid_id &&
                    kid.share_class === selectedShareClass.share_class
                    ? { ...kid, version: newVersion }
                    : kid,
                ),
              };
            });
            setLocalFunds(newFunds);

            // After updating, find the updated fund and kids
            const updatedFund = newFunds.find(
              (f) => f.fund_id_string === selectedFund.fund_id_string,
            );
            if (updatedFund) {
              const sortedKids = [...updatedFund.kids]; // already sorted by ISIN
              const currentIndex = sortedKids.findIndex(
                (kid) =>
                  kid.kiid_id === selectedShareClass.kiid_id &&
                  kid.share_class === selectedShareClass.share_class,
              );

              // Search for next unreviewed, starting after current, wrapping around
              let nextUnreviewedKid: KIDInfo | undefined = undefined;
              for (let i = 1; i <= sortedKids.length; i++) {
                const idx = (currentIndex + i) % sortedKids.length;
                if (!isShareClassReviewed(sortedKids[idx].version)) {
                  nextUnreviewedKid = sortedKids[idx];
                  break;
                }
              }

              // If there's an unreviewed kid, select it; otherwise, set to null
              if (nextUnreviewedKid) {
                setSelectedShareClass(nextUnreviewedKid);
              } else {
                setSelectedShareClass(null);
              }
            }
            // --- End optimistic update ---
          }

          setReviewComplete(true);

          // Invalidate the kids-overview-data query to trigger a refetch
          queryClient.invalidateQueries({ queryKey: ['kids-overview-data'] });
        }
      },
    });

    const handleClose = (event: any, reason: string) => {
      // Don't allow closing if the request is running or review is complete
      if (markDocumentReviewedMutation.isPending || reviewComplete) {
        return;
      }
      if (reason !== 'backdropClick') {
        setDialogIsShowing(false);
      }
    };

    const reviewNextDocument = () => {
      if (!selectedFund || !selectedShareClass) return;

      setDialogIsShowing(false);

      navigate(
        isPriips
          ? `/raidr/priips-kids-review?fundId=${selectedFund.fund_id_string}&isin=${selectedShareClass.share_class
          }&documentLanguage=${selectedShareClass.document_language}&currentVersion=${selectedShareClass.version
          }&publishedVersion=${selectedShareClass.version.split('.')[0] + '.0.0'}`
          : `/raidr/ucits-kiids-review?fundId=${selectedFund.fund_id_string}&isin=${selectedShareClass.share_class
          }&documentLanguage=${selectedShareClass.document_language}&currentVersion=${selectedShareClass.version
          }&publishedVersion=${selectedShareClass.version.split('.')[0] + '.0.0'}`,
      );
    };

    return (
      <GenericModal
        isOpen={dialogIsShowing}
        setIsOpen={setDialogIsShowing}
        preventClose={markDocumentReviewedMutation.isPending || reviewComplete}
        onClose={handleClose}
        title={!reviewComplete ? 'Confirm Review?' : 'Review Complete'}
        description={
          !reviewComplete
            ? 'Update the version number of the document to indicate that a review has been completed.'
            : 'Would you like to review another document? Select the fund and share class below:'
        }
        className="max-w-4xl"
        titleClassName="text-center"
        descriptionClassName="text-center"
      >
        {!reviewComplete ? (
          <div className="mt-4 flex justify-center space-x-4">
            {hasEditBtn && (
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onClick={() =>
                  navigate(
                    isPriips
                      ? `/raidr/priips-kids-editor?fundId=${fundId}&isin=${shareClass}&documentLanguage=${documentLanguage}`
                      : `/raidr/ucits-kiids-editor?fundId=${fundId}&isin=${shareClass}&documentLanguage=${documentLanguage}`,
                  )
                }
              >
                Go To Edit Page
              </button>
            )}
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => setDialogIsShowing(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => markDocumentReviewedMutation.mutate()}
              disabled={
                markDocumentReviewedMutation.isPending ||
                isSelectedShareClassReviewed
              }
            >
              {markDocumentReviewedMutation.isPending ? (
                <>
                  <FiLoader className="-ml-1 mr-2 h-5 w-5 animate-spin" />
                  Confirming...
                </>
              ) : (
                'Confirm'
              )}
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-col items-center space-y-8">
              <div className="w-full max-w-2xl rounded-lg border border-gray-100 bg-gray-50/50 p-6 shadow-sm">
                <div className="space-y-6">
                  <div className="w-full">
                    <Listbox
                      value={selectedFund}
                      by="fund_id_string"
                      onChange={(fund: UniqueFund) => {
                        setSelectedFund(fund);
                        // Find the first unreviewed share class, or the first one if all are reviewed
                        const firstUnreviewedKid = fund.kids.find(
                          (kid) => !isShareClassReviewed(kid.version),
                        );
                        setSelectedShareClass(firstUnreviewedKid || fund.kids[0]);
                      }}
                    >
                      <div className="relative">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Select Fund
                        </label>
                        <ListboxButton
                          className={`relative w-full ${isFundFullyReviewed
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer'
                            } rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300 sm:text-sm`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="block truncate">
                              {selectedFund
                                ? `${selectedFund.fund_id_string} - ${selectedFund.fund_name}`
                                : 'Select a fund'}
                            </span>
                            {selectedFund && (
                              <span
                                className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${isFundFullyReviewed
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                                  }`}
                              >
                                {
                                  selectedFund.kids.filter((kid) =>
                                    isShareClassReviewed(kid.version),
                                  ).length
                                }
                                /{selectedFund.kids.length} Reviewed
                              </span>
                            )}
                          </div>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <FiChevronDown
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </span>
                        </ListboxButton>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <ListboxOptions className="absolute z-[60] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {fundsForUI.map((fund) => {
                              const isFullyReviewed = fund.kids.every((kid) =>
                                isShareClassReviewed(kid.version),
                              );
                              const reviewedCount = fund.kids.filter((kid) =>
                                isShareClassReviewed(kid.version),
                              ).length;
                              return (
                                <ListboxOption
                                  key={fund.fund_id_string}
                                  className={`relative select-none py-2 pl-10 pr-4 text-gray-900 data-[focus]:!bg-blue-100 data-[selected]:bg-blue-50 data-[focus]:text-blue-900 ${isFullyReviewed ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                  value={fund}
                                  disabled={isFullyReviewed}
                                >
                                  {({ selected }) => (
                                    <>
                                      <div className="flex items-center justify-between gap-2">
                                        <span
                                          className={`block truncate ${selected
                                            ? 'font-medium'
                                            : 'font-normal'
                                            }`}
                                        >
                                          {`${fund.fund_id_string} - ${fund.fund_name}`}
                                        </span>
                                        <span
                                          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${isFullyReviewed
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                            }`}
                                        >
                                          {reviewedCount}/{fund.kids.length}{' '}
                                          Reviewed
                                        </span>
                                      </div>
                                      {selected ? (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                          <FiCheck
                                            className="h-5 w-5"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </ListboxOption>
                              );
                            })}
                          </ListboxOptions>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  <div className="w-full">
                    <Listbox
                      value={selectedShareClass}
                      onChange={setSelectedShareClass}
                      disabled={!selectedFund}
                    >
                      <div className="relative">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Select Share Class
                        </label>
                        <ListboxButton
                          className={`relative w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300 sm:text-sm ${!selectedFund
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                            }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="block truncate">
                              {selectedShareClass
                                ? `${selectedShareClass.share_class} - ${selectedShareClass.share_class_name}`
                                : 'Select a share class'}
                            </span>
                            {selectedShareClass &&
                              isShareClassReviewed(
                                selectedShareClass.version,
                              ) && (
                                <span className="inline-flex shrink-0 items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                  Reviewed
                                </span>
                              )}
                          </div>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <FiChevronDown
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </span>
                        </ListboxButton>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <ListboxOptions className="absolute z-[60] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {shareClassesForFund.map((kid) => {
                              const isReviewed = isShareClassReviewed(
                                kid.version,
                              );
                              return (
                                <ListboxOption
                                  key={`${kid.kiid_id}-${kid.share_class}`}
                                  className={`relative select-none py-2 pl-10 pr-4 text-gray-900 data-[focus]:!bg-blue-100 data-[selected]:bg-blue-50 data-[focus]:!text-blue-900 ${isReviewed ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                  value={kid}
                                  disabled={isReviewed}
                                >
                                  {({ selected }) => (
                                    <>
                                      <div className="flex items-center justify-between gap-2">
                                        <span
                                          className={`block truncate ${selected
                                            ? 'font-medium'
                                            : 'font-normal'
                                            }`}
                                        >
                                          {`${kid.share_class} - ${kid.share_class_name}`}
                                        </span>
                                        {isReviewed && (
                                          <span className="inline-flex shrink-0 items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                            Reviewed
                                          </span>
                                        )}
                                      </div>
                                      {selected ? (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                          <FiCheck
                                            className="h-5 w-5"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </ListboxOption>
                              );
                            })}
                          </ListboxOptions>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center space-x-4">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onClick={() => navigate(backToOverviewLink)}
              >
                Return To Overview
              </button>
              {overviewData.length > 0 && (
                <div className="group relative">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={reviewNextDocument}
                    disabled={
                      !selectedFund ||
                      !selectedShareClass ||
                      isSelectedShareClassReviewed
                    }
                  >
                    Review Selected Document
                  </button>
                  {(!selectedFund ||
                    !selectedShareClass ||
                    isSelectedShareClassReviewed) && (
                      <div className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                        {!selectedFund
                          ? 'Please select a fund'
                          : !selectedShareClass
                            ? 'Please select a share class'
                            : 'Selected document is already reviewed'}
                      </div>
                    )}
                </div>
              )}
            </div>
          </>
        )}
      </GenericModal>
    );
  };

export default ReviewConfirmationDialogBox;
